<script setup lang="ts">
import { computed } from 'vue';
import LiveTrackCard from '@/components/LiveTrackCard.vue';
import SeverityBadge from '@/components/AudioFileIssues/SeverityBadge.vue';
import TrackRatingBadge from '@/components/AudioFileIssues/TrackRatingBadge.vue';
import { Tooltip, TooltipContent, TooltipRow, TooltipTrigger } from '@/components/ui/tooltip';
import { CalendarPlus, Upload } from 'lucide-vue-next';
import { Popover, PopoverClose, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { XMarkIcon } from '@heroicons/vue/24/solid';
import type { AudioFileIssueRow } from '@/composables/api/useAudioFileIssuesApi';
import { formatUsMmDdYyTime12h } from '@/utils/date';

/**
 * Inspector-style card surface for a single audio-file-issues row.
 *
 * Mirrors the LiveTrack header card from {@link
 * file:src/components/AudioFileIssueInspector.vue} (cover on the left,
 * title/artist/album on the right, copyright + duration pinned to the bottom
 * of the cover column, bitrate + ISRC pinned to the bottom of the right
 * column) so the page-level "Cards" view reads as a fleet of inspector
 * previews. The action-button row from the inspector is replaced with the
 * severity pill (Error / Warning / Info — same as the filter builder and
 * {@link SeverityBadge}) so the headline reads at a glance, followed by
 * {@link TrackRatingBadge} when the row includes `rating`; the "+N issues"
 * tooltip still lists the concrete issue-type labels for this track.
 *
 * Footer timestamps use **`track_created_at`**, **`track_uploaded_date`**, and
 * **`uploaded_date`** (file last update) in
 * `MM/DD/YY h:mm AM/PM`. The live track code is pinned to the top-right of
 * the card so it stays visible while titles truncate.
 *
 * Container-query (`@container` + `@xs:flex-row`) layout means the card
 * adapts to its grid cell, not the viewport: a 4-column desktop grid stays
 * horizontal; a single-column phone grid stacks gracefully.
 */
const props = defineProps<{
  row: AudioFileIssueRow;
  /** Optional ordered list of every other issue type on the same track (slug → label resolved upstream). */
  otherIssueTypeLabels?: string[];
  /**
   * Whether this card is the active selection (driven by the page when split-view
   * mode is on). Active cards get a stronger border/ring so the selection is
   * obvious next to the inline detail pane. Defaults to false in standalone use.
   */
  active?: boolean;
}>();

const emit = defineEmits<{
  (e: 'click', row: AudioFileIssueRow, event: MouseEvent): void;
}>();

const issuesCount = computed(() => props.row.issues_count ?? 1);

/** Every issue type on this track (primary first, then the rest) — shown when the severity badge is clicked. */
const allIssueLabels = computed(() =>
  [props.row.issue_type_label, ...(props.otherIssueTypeLabels ?? [])].filter(Boolean),
);

/**
 * Extra classes layered onto {@link LiveTrackCard}'s base Card: the hover /
 * cursor / padding affordances plus the active-selection ring when the page is
 * in split-view mode.
 */
const cardClass = computed(() =>
  [
    'hover:bg-muted/30 hover:border-border cursor-pointer p-3 transition-colors',
    props.active ? 'border-primary bg-muted/40 ring-1 ring-primary/40' : '',
  ]
    .filter(Boolean)
    .join(' '),
);

function formatDuration(totalSeconds: number | null | undefined): string {
  if (totalSeconds == null || !Number.isFinite(totalSeconds) || totalSeconds < 0) {
    return '';
  }
  const totalWhole = Math.round(totalSeconds);
  const minutes = Math.floor(totalWhole / 60);
  const seconds = totalWhole % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

const trackTitle = computed(() => {
  const name = props.row.live_track_name ?? '';
  const remix = props.row.remix_name ?? '';
  if (!name) return props.row.live_track_code;
  return remix ? `${name} (${remix})` : name;
});

/** PlayTrackButton props for the cover overlay (full-bleed play target on the art). */
const coverPlay = computed(() => ({
  kind: 'live_track' as const,
  liveTrackCode: props.row.live_track_code,
  hasAudio: Boolean(props.row.live_track_code),
  title: trackTitle.value,
  artist: props.row.live_track_artist_name,
  album: props.row.album_name,
  coverUrl: props.row.cover_art_signed_url,
  // Records for the player bar's identity lines. The issues payload joins the
  // artist id but only the album NAME, so the album line stays plain text.
  liveTrackId: props.row.live_track_id,
  artistId: props.row.live_track_artist_id,
}));

function onCardClick(event: MouseEvent) {
  emit('click', props.row, event);
}

function onCardKeydown(event: KeyboardEvent) {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    emit('click', props.row, event as unknown as MouseEvent);
  }
}
</script>

<template>
  <LiveTrackCard
    :card-class="cardClass"
    role="button"
    tabindex="0"
    :data-testid="`audio-file-issue-card-${row.live_track_code}`"
    :data-active="active ? 'true' : 'false'"
    :aria-pressed="active ? 'true' : 'false'"
    :aria-label="`Open inspector for ${trackTitle} (${row.live_track_code})`"
    :cover-url="row.cover_art_signed_url"
    :copyright="row.copyright"
    :duration="formatDuration(row.duration)"
    :title="trackTitle"
    :title-tooltip="trackTitle"
    title-truncate
    :artist="row.live_track_artist_name"
    :album="row.album_name"
    row-align="stretch"
    cover-meta-to-bottom
    meta-column-padding="pr-0 @xs:pr-[5.75rem]"
    :cover-play="coverPlay"
    :cover-expand-testid="`audio-file-issue-card-expand-cover-${row.live_track_code}`"
    @click="onCardClick"
    @keydown="onCardKeydown"
  >
    <template #corner>
      <div
        class="text-muted-foreground absolute top-3 right-3 z-[1] flex max-w-[calc(100%-6.5rem)] flex-col items-end gap-0.5 font-mono leading-none tabular-nums tracking-tight @xs:max-w-[12rem]"
      >
        <span
          class="pointer-events-none truncate text-[11px]"
          :title="row.live_track_code"
          aria-hidden="true"
        >
          {{ row.live_track_code }}
        </span>
        <Tooltip v-if="row.playlist_count != null">
          <TooltipTrigger as-child>
            <span class="cursor-help text-[10px]">{{ row.playlist_count }}</span>
          </TooltipTrigger>
          <TooltipContent side="top" align="end">On Playlists</TooltipContent>
        </Tooltip>
      </div>
    </template>

    <!-- Severity pill (matches filter dropdown / SeverityBadge). Issue-type
         strings (e.g. "Original-bitrate backup missing") stay in the inspector
         and in the "+N" tooltip list, not as the card headline. -->
    <template #badges>
      <div class="mt-2 flex flex-wrap items-center gap-2">
        <!-- Severity pill is a popover trigger: click it to list every issue on this
             track as single-line items. @click / @keydown .stop so activating the pill
             doesn't also select the card (the card root is role="button"). -->
        <Popover>
          <PopoverTrigger as-child>
            <button
              type="button"
              class="focus-visible:ring-ring rounded focus-visible:outline-none focus-visible:ring-1"
              :aria-label="`${issuesCount} issue${issuesCount === 1 ? '' : 's'} on this track — show list`"
              @click.stop
              @keydown.enter.stop
              @keydown.space.stop
            >
              <SeverityBadge
                :severity="row.severity"
                size="md"
                class="cursor-pointer text-sm font-semibold"
              />
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" side="top" class="w-64 p-2" @click.stop>
            <div class="mb-1 flex items-center justify-between gap-2 px-1">
              <p class="text-muted-foreground text-[11px] font-medium uppercase tracking-wide">
                Issues for this track
              </p>
              <PopoverClose
                class="text-muted-foreground hover:text-foreground hover:bg-muted focus-visible:ring-ring -mr-1 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded focus-visible:outline-none focus-visible:ring-1"
                aria-label="Close issues list"
              >
                <XMarkIcon class="h-3.5 w-3.5" aria-hidden="true" />
              </PopoverClose>
            </div>
            <ul class="flex flex-col">
              <li
                v-for="label in allIssueLabels"
                :key="label"
                class="truncate rounded px-1 py-1 text-sm"
                :title="label"
              >
                {{ label }}
              </li>
            </ul>
          </PopoverContent>
        </Popover>
        <TrackRatingBadge :rating="row.rating" class="text-sm font-semibold" />
        <span v-if="row.genre_name" class="text-muted-foreground truncate text-sm">
          {{ row.genre_name }}
        </span>
      </div>
    </template>

    <template #meta>
      <div
        class="text-muted-foreground mt-auto flex flex-wrap items-baseline gap-x-4 gap-y-1 pt-2 font-mono text-[11px] leading-none tabular-nums"
      >
        <Tooltip v-if="row.bitrate != null">
          <TooltipTrigger as-child>
            <span class="whitespace-nowrap">{{ row.bitrate }} kbps</span>
          </TooltipTrigger>
          <TooltipContent>DB Bitrate</TooltipContent>
        </Tooltip>
        <span v-if="row.isrc" class="whitespace-nowrap">ISRC {{ row.isrc }}</span>
        <Tooltip v-if="row.track_created_at">
          <TooltipTrigger as-child>
            <span class="max-w-[10rem] cursor-default truncate whitespace-nowrap">{{
              formatUsMmDdYyTime12h(row.track_created_at)
            }}</span>
          </TooltipTrigger>
          <TooltipContent align="start" side="bottom">
            <TooltipRow :icon="CalendarPlus" label="Track created" class="tabular-nums">{{ formatUsMmDdYyTime12h(row.track_created_at) }}</TooltipRow>
          </TooltipContent>
        </Tooltip>
        <Tooltip v-if="row.track_uploaded_date">
          <TooltipTrigger as-child>
            <span class="max-w-[10rem] cursor-default truncate whitespace-nowrap">{{
              formatUsMmDdYyTime12h(row.track_uploaded_date)
            }}</span>
          </TooltipTrigger>
          <TooltipContent align="start" side="bottom">
            <TooltipRow :icon="Upload" label="Track uploaded" class="tabular-nums">{{ formatUsMmDdYyTime12h(row.track_uploaded_date) }}</TooltipRow>
          </TooltipContent>
        </Tooltip>
        <Tooltip v-if="row.uploaded_date">
          <TooltipTrigger as-child>
            <span class="max-w-[10rem] cursor-default truncate whitespace-nowrap">{{
              formatUsMmDdYyTime12h(row.uploaded_date)
            }}</span>
          </TooltipTrigger>
          <TooltipContent align="start" side="bottom">
            <TooltipRow :icon="Upload" label="File uploaded" class="tabular-nums">{{ formatUsMmDdYyTime12h(row.uploaded_date) }}</TooltipRow>
          </TooltipContent>
        </Tooltip>
      </div>
    </template>
  </LiveTrackCard>
</template>
